import { z } from 'zod';
import { criterionSchema } from './criterion';
import { formStepSchema } from './form-step';
import { FormContentType } from '../../constants';

const languageMessagesSchema = z.object({
  messages: z.object({
    qualified: z.string().min(1, { message: 'Qualified message is required' }),
    not_qualified: z.string().min(1, { message: 'Not qualified message is required' }),
    duplicated: z.string().min(1, { message: 'Duplicated message is required' }),
  }),
});
export type LanguageMessagesInput = z.infer<typeof languageMessagesSchema>;

/**
 * Spanish is a secondary locale: its result messages accept an empty string and default
 * to one, so a form can be saved with English copy alone.
 */
const optionalLanguageMessagesSchema = z.object({
  messages: z.object({
    qualified: z.string().default(''),
    not_qualified: z.string().default(''),
    duplicated: z.string().default(''),
  }),
});

export const formSchema = z
  .object({
    id: z.number().int().positive().optional(),
    name: z.string().min(1).max(255),
    criteria: z.array(z.array(criterionSchema)).min(1),
    metadata: z.object({
      i18n: z.object({
        en: languageMessagesSchema,
        es: optionalLanguageMessagesSchema,
      }),
    }),
    claim_section_id: z.preprocess(
      (value) => (value === 0 || value === null || Number.isNaN(value) ? undefined : value),
      z.number().int().positive().optional()
    ),
    form_steps: z.array(formStepSchema).min(1),
  })
  .superRefine((data, ctx) => {
    const hasQuestion = data.form_steps.some((step) =>
      step.form_steps_content.some(
        (content) =>
          content.type === FormContentType.QUESTION &&
          Array.isArray(content.data) &&
          content.data.length > 0
      )
    );

    // When the form collects at least one question, its qualification criteria must
    // hold at least one rule with an actual value — an empty rule qualifies nobody.
    if (hasQuestion) {
      const hasRuleWithValue = data.criteria.some((group) =>
        group.some((criterion) => criterion.expected.some((value) => String(value).trim() !== ''))
      );
      if (!hasRuleWithValue) {
        ctx.addIssue({
          code: 'custom',
          path: ['criteria'],
          message: 'Add at least one criteria rule with a value when the form has questions',
        });
      }
    }

    const contactStepIndexes: number[] = [];
    let contactCount = 0;

    data.form_steps.forEach((step, stepIndex) => {
      const count = step.form_steps_content.filter(
        (content) => FormContentType.CONTACT === content.type
      ).length;
      if (count > 0) contactStepIndexes.push(stepIndex);
      contactCount += count;
    });

    // A form must collect a lead, so exactly one contact block is required.
    if (contactCount === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['form_steps'],
        message: 'A form must contain a contact block',
      });
    }

    // A form can hold at most one contact block across every step.
    if (contactCount > 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['form_steps'],
        message: 'A form can contain only one contact block',
      });
    }

    // The contact block, if present, must live in the last step.
    const lastStepIndex = data.form_steps.length - 1;
    if (contactStepIndexes.some((index) => index !== lastStepIndex)) {
      ctx.addIssue({
        code: 'custom',
        path: ['form_steps'],
        message: 'The contact block must be placed in the last step',
      });
    }
  });
export type FormInput = z.infer<typeof formSchema>;
